import { useState, useEffect } from "react";
import { X, ArrowRight, CreditCard, Lock, Check, Loader } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const CATS = ["Rental property", "GST", "Vehicle expenses", "Bright-line / property", "Home office", "Sole trader / contractor", "Other"];

export default function AskSheet({ onClose, credits, onSubmitted }) {
  const [step, setStep] = useState(1);
  const [cat, setCat] = useState("Rental property");
  const [q, setQ] = useState("");
  const [plan, setPlan] = useState(credits > 0 ? "use_credit" : "single_67");
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState("");

  useEffect(() => { if (credits > 0) setPlan("use_credit"); }, [credits]);

  const price = plan === "single_67" ? 67 : plan === "pack_127" ? 127 : 0;
  const gst = (price * 3 / 23).toFixed(2);

  const submit = async () => {
    if (q.trim().length < 20) { toast.error("Please write at least 20 characters"); return; }
    setBusy(true);
    try {
      if (plan === "use_credit") {
        const { data } = await api.post("/questions/submit", { category: cat, question_text: q, plan_type: "use_credit" });
        setRef(data.ref); setStep(3);
        toast.success("Question submitted"); onSubmitted && onSubmitted();
      } else {
        const { data } = await api.post("/payments/checkout", {
          plan_type: plan, category: cat, question_text: q,
          origin_url: window.location.origin,
        });
        toast.info("Opening secure Stripe checkout…");
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start checkout");
    } finally { setBusy(false); }
  };

  return (
    <div className="overlay" onClick={onClose} data-testid="ask-sheet">
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-head">
          <div className="sheet-title">Ask a <em>question</em></div>
          <button onClick={onClose} data-testid="ask-close"><X size={22} /></button>
        </div>
        <div className="sheet-body">
          {step === 1 && (
            <>
              <div>
                <label className="at-label">Category</label>
                <select className="at-select" value={cat} onChange={e => setCat(e.target.value)} data-testid="ask-category">
                  {CATS.map(c => <option key={c}>{c}</option>)}
                </select>
              </div>
              <div className="mt-4">
                <label className="at-label">Your question</label>
                <textarea className="at-textarea" maxLength={500} placeholder="Describe your situation and what you'd like to know. The more detail, the better the answer." value={q} onChange={e => setQ(e.target.value)} data-testid="ask-question" />
                <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>One clear question per submission · {q.length}/500</div>
              </div>
              {credits > 0 ? (
                <div className="mt-6 p-4 border-2 rounded" style={{ borderColor: "var(--ink)", background: "#e4efe7" }} data-testid="credits-notice">
                  <div className="font-display font-bold uppercase tracking-wide text-sm">Using 1 of your {credits} prepaid questions</div>
                  <div className="text-sm mt-1 font-serif italic">No payment needed — you already paid for this in your 3-pack.</div>
                </div>
              ) : (
                <div className="mt-6">
                  <label className="at-label">Choose your plan</label>
                  <div className="grid grid-cols-2 gap-3">
                    <div onClick={() => setPlan("single_67")} className="at-card p-5 cursor-pointer" style={{ borderColor: plan === "single_67" ? "var(--kiwi)" : "var(--ink)", boxShadow: plan === "single_67" ? "3px 3px 0 var(--kiwi)" : "3px 3px 0 var(--ink)" }} data-testid="plan-single">
                      <div className="font-display font-bold uppercase tracking-wide text-sm">Single question</div>
                      <div className="font-display font-black text-3xl mt-1">$67</div>
                      <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>One answer · incl. GST</div>
                    </div>
                    <div onClick={() => setPlan("pack_127")} className="at-card p-5 cursor-pointer" style={{ borderColor: plan === "pack_127" ? "var(--amber)" : "var(--ink)", boxShadow: plan === "pack_127" ? "3px 3px 0 var(--amber)" : "3px 3px 0 var(--ink)" }} data-testid="plan-pack">
                      <span className="at-tag" style={{ background: "var(--amber)", color: "var(--ink)" }}>Best value</span>
                      <div className="font-display font-bold uppercase tracking-wide text-sm mt-2">3 questions</div>
                      <div className="font-display font-black text-3xl mt-1">$127</div>
                      <div className="text-xs mt-1" style={{ color: "var(--muted)" }}>Use any time · saves $74</div>
                    </div>
                  </div>
                  <div className="mt-3 text-xs" style={{ color: "var(--muted)" }}>
                    <b>${price}</b> incl. ${gst} GST · secure payment by Stripe (test mode)
                  </div>
                </div>
              )}
              <button onClick={submit} disabled={busy} className="at-btn block mt-6" data-testid="ask-continue">
                {busy ? <><Loader size={16} className="animate-spin" /> Working…</> : plan === "use_credit" ? <>Submit question <ArrowRight size={16} /></> : <><CreditCard size={16} /> Continue to secure payment</>}
              </button>
              {plan !== "use_credit" && (
                <div className="mt-3 flex items-center gap-2 text-xs" style={{ color: "var(--muted)" }}>
                  <Lock size={13} /> You'll be redirected to Stripe. Card details never touch our server.
                </div>
              )}
            </>
          )}
          {step === 3 && (
            <div className="text-center py-6" data-testid="ask-confirm">
              <div className="w-16 h-16 mx-auto rounded-full grid place-items-center" style={{ background: "var(--kiwi)", color: "var(--cream-2)" }}>
                <Check size={32} strokeWidth={3} />
              </div>
              <div className="mt-6 font-display font-black text-3xl uppercase">Question submitted</div>
              <div className="mt-3 font-serif italic text-lg" style={{ color: "var(--muted)" }}>
                Reference <b>{ref}</b>. Your question is with your tax agent — you'll see the answer here within one business day.
              </div>
              <button onClick={onClose} className="at-btn mt-8" data-testid="ask-done">View my questions</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
